<template>
    <div>
        <v-card elevation="0" flat>
            <v-card-text>
                <v-row>
                    <v-col cols="12" class="font-weight-bold">I. Introduction</v-col>
                    <v-col cols="12">
                        King’s University College, a Catholic liberal arts institution affiliated with Western University, upholds its identity as a Catholic community within a larger secular university setting.
                    </v-col>
                    <v-col cols="12">
                        The <strong>Board of Directors</strong> holds final administrative authority over the College’s property, operations, and conduct.
                    </v-col>
                    <v-col cols="12">
                        All student residents must follow:
                        <ul>
                            <li>The <strong>King’s University College Code of Student Conduct</strong></li>
                            <li>The <strong>Rules of Residence</strong></li>
                            <li>All applicable <strong>Canadian and Ontario laws</strong></li>
                        </ul>
                    </v-col>
                    <v-col cols="12">
                        College procedures operate independently of external legal proceedings, which means disciplinary action may proceed regardless of any ongoing civil or criminal cases.
                    </v-col>
                    <v-col cols="12">
                        <strong>Note:</strong> Rules may be updated during the academic year.
                    </v-col>
                    <v-col cols="12"><v-divider></v-divider></v-col>
                    <v-col cols="12" class="font-weight-bold">II. Residence Rules and Offences</v-col>
                    <v-col cols="12">
                        A rule violation occurs when a resident endangers others or behaves in ways that conflict with the College’s values and community standards. The following are prohibited:
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">A. Serious Offences (May Lead to Immediate Expulsion)</v-col>
                    <v-col cols="12">
                        <ol>
                            <li>
                                <strong>Weapons & Hazardous Materials</strong><br />
                                Possession of weapons, fireworks, explosives, chemicals, or any hazardous or illegal materials is strictly forbidden.
                            </li>
                            <li>
                                <strong>Threatening or Harassing Behaviour</strong><br />
                                Any actions—physical, verbal, or psychological—that harm or threaten others’ wellbeing are prohibited. <br />
                                <i>Complaints of harassment based on prohibited grounds (e.g., race, gender, religion, etc.) will be handled under the Ontario Human Rights Code and relevant College policies.</i>
                            </li>
                            <li>
                                <strong>Theft or Possession of Stolen Property</strong><br />
                            </li>
                            <li>
                                <strong>Illegal Drug Use or Possession</strong><br />
                                Illegal drugs and related paraphernalia are not allowed in residence. Guests found in violation will be banned, and the host may be expelled.<br />
                                <strong>Cannabis Policy</strong><br />
                                <ul>
                                    <li>Legal-age residents (19+) may possess up to 30g of dried cannabis in <strong>airtight containers</strong>.</li>
                                    <li>Smoking or vaping cannabis is <strong>prohibited</strong> anywhere on campus, including residences and parking lots.</li>
                                    <li>Cannabis consumption in <strong>non-smoking forms</strong> is allowed in private rooms (19+ only).</li>
                                    <li><strong>Cannabis plants</strong>, cooking/baking, distribution, or mailing through campus services is not allowed.</li>
                                    <li>Smells or attempts to cover up cannabis use will be treated as evidence of rule violation.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Tampering with Fire Safety Equipment</strong><br />
                                Includes setting false alarms, and tampering with fire doors, extinguishers, detectors, etc.
                            </li>
                            <li>
                                <strong>Property Damage</strong><br />
                                Any damage to College or personal property.
                            </li>
                        </ol>
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">B. Additional Offences</v-col>
                    <v-col cols="12">
                        <ol>
                            <li>
                                <strong>Ignoring Fire Alarms</strong><br />
                                Failing to evacuate during an alarm.
                            </li>
                            <li>
                                <strong>Underage Alcohol Use & Alcohol Misuse</strong>
                                <ul>
                                    <li>No possession/consumption by or provision to anyone under 19.</li>
                                    <li>Alcohol use is only permitted in designated areas by those 19+ and must follow the Liquor Licence Act and the King’s Alcohol Policy.</li>
                                </ul>
                                <strong>Alcohol Guidelines</strong>
                                <ul>
                                    <li>Permitted only in private rooms and lounges.</li>
                                    <li>No drinking in public spaces (e.g., hallways, stairwells).</li>
                                    <li>Bottled beer, kegs, drinking games, or excessive drinking are not allowed.</li>
                                    <li>No open alcohol outside residence buildings.</li>
                                    <li>Alcohol intoxication is not an acceptable excuse for misconduct.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Building Security Violations</strong><br />
                                Includes unauthorized key duplication, propping doors open, or disabling window mechanisms.
                            </li>
                            <li>
                                <strong>Misuse of Student ID or Meal Card</strong><br />
                                Includes failure to show ID when requested by College personnel.
                            </li>
                            <li>
                                <strong>Unauthorized Entry</strong><br />
                                Includes forced entry or remaining in an area after being asked to leave.
                            </li>
                            <li>
                                <strong>Guest Policy Violations</strong>
                                <ul>
                                    <li>Residents are responsible for their guests and must accompany them at all times.</li>
                                    <li>Maximum of two guests per resident.</li>
                                    <li>Overnight guests are limited to two nights per term and must be registered.</li>
                                    <li>No guests during certain periods (e.g., exams, Orientation Week).</li>
                                    <li>Guests cannot have keys or access cards.</li>
                                    <li>Misconduct by a guest will result in disciplinary action for the host.</li>
                                    <li>Overnight guests require advance approval from all roommates.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Disruption of Quiet Hours</strong>
                                <ul>
                                    <li>Noise must not disturb others. This includes music, TV, and loud conversation.</li>
                                    <li>Quiet Hours:
                                        <ul>
                                            <li>Sunday–Friday: 7:00 p.m. – 9:00 a.m.</li>
                                            <li>Friday–Saturday: 1:00 a.m. – 9:00 a.m.</li>
                                            <li>During exam periods: 22-hour quiet with a 2-hour moderate noise window (4–6 p.m.)</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>Smoking/Vaping</strong>
                                <ul>
                                    <li>Smoking or vaping is banned inside all campus buildings, including residences.</li>
                                    <li>Only allowed in designated outdoor areas.</li>
                                    <li>Violations may lead to fines or damage charges.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Pets</strong><br />
                                Pets are not allowed. Service animals including emotional support animals require prior approval from the Residence Manager.
                            </li>
                            <li>
                                <strong>Furniture Use</strong>
                                <ul>
                                    <li>Students are responsible for room furniture and will be charged for missing or damaged items.</li>
                                    <li>Shared damage costs will apply if responsibility cannot be determined.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Cleanliness</strong><br />
                                Residents must keep personal and common spaces tidy. Extra cleaning fees may apply.
                            </li>
                            <li>
                                <strong>Decorating Restrictions</strong>
                                <ul>
                                    <li>No painting, wallpapering, or damaging wall surfaces.</li>
                                    <li>Decorations must not pose fire hazards.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Furniture Relocation</strong>
                                <ul>
                                    <li>Lounge or room furniture must stay in place.</li>
                                    <li>Screens or window mechanisms must not be removed.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Exterior Posting or Throwing Objects</strong><br />
                                Nothing may be attached to building exteriors or thrown from windows.
                            </li>
                            <li>
                                <strong>Posting in Public Areas</strong>
                                <ul>
                                    <li>All public displays must be approved by the Residence Manager.</li>
                                    <li>Offensive materials must be removed upon request.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Electrical Equipment</strong>
                                <ul>
                                    <li>Power bars with fuses are required.</li>
                                    <li>Small refrigerators are allowed (max. 5 cu. ft., 35” high).</li>
                                    <li>No modification of electrical wiring.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>RezNet Policy</strong><br />
                                Students must follow King’s and Western’s IT policies.
                            </li>
                            <li>
                                <strong>Cooking Appliances</strong>
                                <ul>
                                    <li>No cooking or warming devices in rooms.</li>
                                    <li>Use kitchenette only.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Open Flames</strong><br />
                                No candles or incense. Religious exceptions must be approved.
                            </li>
                            <li>
                                <strong>Bicycles and Skates</strong><br />
                                Not permitted inside residence buildings.
                            </li>
                            <li>
                                <strong>Parking Policy</strong><br />
                                All residents must follow the King’s Parking Policy.
                            </li>
                            <li>
                                <strong>Laundry Facilities</strong><br />
                                Use is restricted to posted hours.
                            </li>
                            <li>
                                <strong>Food Services & Meal Plans</strong>
                                <ul>
                                    <li>Meal cards are non-transferable.</li>
                                    <li>Shirts and shoes are required in dining areas.</li>
                                </ul>
                            </li>
                        </ol>
                    </v-col>
                    <v-col cols="12"><v-divider></v-divider></v-col>
                    <v-col cols="12" class="font-weight-bold">III. Penalties for Breaking Residence Rules</v-col>
                    <v-col cols="12">
                        Depending on the seriousness or repetition of an offence, one or more of the following sanctions may be applied:
                    </v-col>
                    <v-col cols="12">
                        <v-simple-table>
                            <tbody>
                                <tr>
                                    <td class="font-weight-bold">Level</td>
                                    <td class="font-weight-bold">Possible Sanctions (may be combined)</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Low</td>
                                    <td>
                                        <ul>
                                            <li>Documented verbal warning</li>
                                            <li>Written warning</li>
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Moderate</td>
                                    <td>
                                        <ul>
                                            <li>Behaviour bond</li>
                                            <li >Fine</li>
                                            <li>Community service</li>
                                            <li>Educational sanction</li>
                                            <li >Replacement cost for damage</li>
                                            <li>Loss of certain privileges (e.g., guest or alcohol privileges)</li>
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">High/Safety Related</td>
                                    <td>
                                        <ul >
                                            <li>Restricted access to parts of the residence</li>
                                            <li >Probation (final warning)</li>
                                            <li >Temporary suspension (up to 72 h)</li>
                                            <li >Expulsion from residence</li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">What each sanction means</v-col>
                    <v-col cols="12">
                        <v-simple-table>
                            <tbody>
                                <tr>
                                    <td class="font-weight-bold">Term</td>
                                    <td class="font-weight-bold">Explanation</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Behavior bond</td>
                                    <td>A refundable deposit ($50 – $300) held for up to the end of the academic year. You forfeit it if you re-offend or miss the payment deadline.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Fine</td>
                                    <td>A cash penalty ($50 – $300) that must be paid within 10 business days of the decision letter.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Community service</td>
                                    <td>Up to <strong>30 hours</strong> of approved service with a deadline set by the Residence Manager.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Educational sanction</td>
                                    <td>A learning or restorative task (e.g., apology letter, workshop, reflection paper).</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Restriction of access</td>
                                    <td>You are barred from specific areas (lounges, floors, buildings, etc.) for a set period.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Probationary warning</td>
                                    <td>Final notice that any further breach will result in expulsion. Parents/guardians are notified if you are under the age of majority.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Temporary suspension</td>
                                    <td>Removal from residence for up to <strong>72 hours</strong> while an investigation or safety concern is addressed. You must arrange (and pay for) your own accommodation.</td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold">Expulsion</td>
                                    <td>Immediate, permanent removal from residence. A trespass notice bars you from all residence areas.</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">Use of fines & forfeited bonds</v-col>
                    <v-col cols="12">Money collected funds student-focused improvements, emergency support, or the King’s Bursary Fund.</v-col>
                    <v-col cols="12" class="font-weight-bold">Non-payment</v-col>
                    <v-col cols="12">Unpaid fines, bonds, or damage charges become overdue accounts handled by Student Financial Services.</v-col>
                    <v-col cols="12">The College may seal your academic record (extra $40 fee), blocking registration and transcript release, until payment is made.</v-col>
                    <v-col cols="12"><v-divider></v-divider></v-col>
                    <v-col cols="12" class="font-weight-bold">IV. Administration & Procedures</v-col>
                    <v-col cols="12">
                        <ol>
                            <li>
                                <strong>Authority</strong>
                                <ul>
                                    <li><strong>Board of Directors</strong> sets residence rules.</li>
                                    <li><strong>Dean of Students</strong> oversees non-academic discipline.</li>
                                    <li><strong>Residence Manager (RM)</strong> and Residence Assistants (RAs) investigate and enforce rules.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Reporting an incident</strong>
                                <ul>
                                    <li><strong>Unsafe situations:</strong> report immediately to the RM or RA.</li>
                                    <li><strong>Other infractions:</strong> report within 24 h (no later than 72 h).</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Notice to the student</strong>
                                <ul>
                                    <li>The RM sends a notice of the alleged offence to your <strong>UWO email</strong> and delivers it in person.</li>
                                    <li>Delivery is considered complete <strong>on the 3rd business day</strong> after the letter is mailed.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Meeting with the RM</strong>
                                <ul>
                                    <li>You must meet promptly with the RM (and possibly the reporting RA) to give your side of the story.</li>
                                    <li>If you skip the meeting, the RM will decide the case without your input.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Decision & Letter</strong>
                                <ul>
                                    <li>The RM issues a written decision—normally within <strong>5 business days</strong> of the meeting (longer during exams/holidays).</li>
                                    <li>The letter states the finding, any penalties, and payment or completion deadlines.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Parental Notification</strong>
                                <ul>
                                    <li>For residents <strong>under the age of majority</strong>, parents/guardians are notified of serious matters, probation, or suspension.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Immediate action</strong>
                                <ul>
                                    <li>When safety is at risk, the College may suspend or expel a student <strong>immediately</strong>. Written reasons follow as soon as possible, and normal review procedures then apply.</li>
                                </ul>
                            </li>
                        </ol>
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">V. Discipline Records</v-col>
                    <v-col cols="12">
                        <ul>
                            <li>A summary of each case—including statements and the decision letter—stays in your <strong>confidential residence file</strong>.</li>
                            <li>These records are <strong>not</strong> part of your official academic transcript and are destroyed <strong>two years</strong> after your last term in residence.</li>
                        </ul>
                    </v-col>
                    <v-col cols="12" class="font-weight-bold">VI. Review (Appeal) Process</v-col>
                    <v-col cols="12">
                        <ol>
                            <li>
                                <strong>Grounds for a Review</strong><br />
                                You may ask the Dean of Students to review the RM’s decision if you believe:
                                <ul>
                                    <li>New, significant information was unavailable at the original meeting.</li>
                                    <li>Procedures were unfair.</li>
                                    <li>The penalty is too severe for the offence.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>How to Request</strong>
                                <ul>
                                    <li>Write a letter to the Dean within <strong>5 business days</strong> of receiving the decision.</li>
                                    <li>State clearly the grounds for review and any supporting details.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Dean’s Initial Response</strong>
                                <ul>
                                    <li>Within <strong>5 business days</strong>, the Dean (or designate) decides whether your request warrants a full review and informs you in writing.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Review Meeting (if granted)</strong>
                                <ul>
                                    <li>Held within <strong>5 business days</strong> of approval.</li>
                                    <li>Attendees may include you, the RM, and others the Dean deems relevant.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Outcome</strong>
                                <ul>
                                    <li>The Dean’s decision is sent to you and the RM, normally within <strong>5 business days</strong> of the review meeting.</li>
                                    <li>A copy remains in your residence discipline file.</li>
                                </ul>
                            </li>
                        </ol>
                    </v-col>
                    <v-col cols="12"><v-divider></v-divider></v-col>
                    <v-col cols="12">
                        Updated: May 2025
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>
<script>
export default {
    name: 'rules-of-residence-may2025',
    props: {
    },
    data() {
        return {
            
        }
    },
    methods: {
    },
    computed: {
        infoColor() {
            return '#5578eb';
        },
        kingsGrey() {
            return '#6d6e71';
        },
        successColor() {
            return '#1bc5bd';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>
<style lang="css" scoped>
div {
    color: black;
    line-height: 1.8;
}
</style>